import React, { useEffect, useMemo, useRef, useState } from 'react';

import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import { IAbstractRecord, IPagination } from '@models';
import { lookupListAsRecordObject } from '@utils/graph-utils';

import {
  treatyTypes,
  newTreatyDrawerSections
} from '@common/constants/drawer.constants';
import {
  useGetRelatedCompanies,
  useGetReinsurers,
  useCreateTreaty,
  useGetCurrencies
} from '@common/slices/drawer.slice';
import {
  INewTreatyFilters,
  CreateTreatyVariables,
  IBaseDrawerProps,
  GetReinsurersVariables
} from '@common/types/drawer.types';
import DynamicFormWidget from '@components/dynamic-form/DynamicFormWidget';
import { RootState } from '@core/redux/store';
import { useSelector } from 'react-redux';

const NewTreatyDrawer: React.FC<IBaseDrawerProps> = ({ onSuccess }) => {
  const submitButtonState = useSelector(
    (state: RootState) => state.apiState.buttonState
  );
  const companyID = '41';
  const [values, setValues] = useState<INewTreatyFilters>({
    company: companyID,
    treatyName: '',
    treatyType: '',
    currency: null,
    effectiveDate: '',
    expiryDate: '',
    underwritingLimit: null,
    claimadviseLimit: null,
    substandardLimit: '',
    reinsurersRepeater: [{ reinsurer: '', share: 0 }]
  });

  const reinsurersList = useRef<Record<string, string>>({});
  const [lovs, setLovs] = useState<Record<string, Record<string, string>>>({
    reinsurerOptions: {}
  });

  const [sections, setSections] = useState<IDynamicFormWidgetSection[]>([]);

  // API Hooks
  const { data: companyData } = useGetRelatedCompanies.useQuery();
  const { data: currencyData } = useGetCurrencies.useQuery();
  const [getReinsurers] = useGetReinsurers.useLazy({ hideSuccessMsg: true });
  const [createTreaty] = useCreateTreaty();

  useEffect(() => {
    if (companyData && currencyData) {
      const companies = companyData?.salesforce?.getRelatedCompanies ?? [];
      const currencies = currencyData?.core?.getCurrencies ?? [];

      const companyLov = lookupListAsRecordObject(
        companies,
        false,
        'id',
        'title'
      );
      const currencyLov = lookupListAsRecordObject(
        currencies,
        false,
        'id',
        'title'
      );
      const currencySymbols = lookupListAsRecordObject(
        currencies,
        false,
        'id',
        'symbol'
      );

      setLovs((prev) => ({
        ...prev,
        company: companyLov,
        currency: currencyLov,
        currencySymbols,
        treatyType: treatyTypes
      }));
    }
  }, [companyData, currencyData]);

  // Update sections only when all lovs are fully populated
  useEffect(() => {
    if (
      lovs.currency &&
      Object.keys(lovs.currency).length > 0 &&
      lovs.treatyType &&
      Object.keys(lovs.treatyType).length > 0 &&
      lovs.reinsurerOptions &&
      Object.keys(lovs.reinsurerOptions).length > 0
    ) {
      setSections(newTreatyDrawerSections(values, lovs));
    }
  }, [lovs, values]);

  // Load reinsurers when currencies are available and not already loaded
  useEffect(() => {
    if (companyData && currencyData) {
      getReinsurers({
        variables: {
          pagination: { pageSize: 1000, pageNumber: 1 },
          companies: [companyID],
          statuses: ['Active'],
          isLocal: null,
          createdDateFrom: null,
          createdDateTo: null,
          orderBy: { attribute: 'createdOn', descending: true },
          keyword: ''
        }
      }).then((res) => {
        const items = res.data?.reinsurance?.searchReinsurers?.items ?? [];
        const options = lookupListAsRecordObject(
          items,
          false,
          'id',
          'fullName'
        );
        updateReinsurersList(options);
        setLovs((prev) => ({
          ...prev,
          reinsurerOptions: { ...options }
        }));
      });
    }
  }, [companyData, currencyData]);

  const handleChange = (
    fieldName: string,
    value: unknown,
    sectionIndex: number,
    allValues: INewTreatyFilters
  ) => {
    if (
      fieldName === 'treatyType' &&
      (value as string).toLowerCase() === 'proportional'
    ) {
      allValues.substandardLimit = '100';
    }

    setValues(allValues);
  };

  const updateReinsurersList = (options: Record<string, string>) => {
    reinsurersList.current = {
      ...reinsurersList.current,
      ...options
    };
  };

  const handleSearch = (
    fieldName: string,
    value: string,
    pagination: IPagination
  ) => {
    return getReinsurers({
      variables: {
        pagination: { pageSize: 1000, pageNumber: 1 },
        companies: [companyID],
        statuses: ['Active'],
        isLocal: null,
        createdDateFrom: null,
        createdDateTo: null,
        orderBy: { attribute: 'createdOn', descending: true },
        keyword: value
      }
    }).then((res) => {
      const items = res.data?.reinsurance?.searchReinsurers?.items ?? [];
      const selectedIds = values.reinsurersRepeater
        ?.map((r) => r.reinsurer)
        ?.filter(Boolean);
      const filteredItems = items.filter(
        (item: any) => !selectedIds.includes(item.id)
      );
      const options = lookupListAsRecordObject(
        filteredItems,
        false,
        'id',
        'fullName'
      );
      updateReinsurersList(options);

      return {
        options,
        totalCount: filteredItems.length
      };
    });
  };

  const onSubmit = async (formValues: IAbstractRecord) => {
    const typedValues = formValues as INewTreatyFilters;

    const payload: CreateTreatyVariables = {
      createTreatyInput: {
        name: typedValues.treatyName,
        company: companyID,
        type: typedValues.treatyType,
        currency: typedValues.currency,
        treatyEffectiveDate: typedValues.effectiveDate,
        treatyExpiryDate: typedValues.expiryDate,
        underwritinglimit: Number(typedValues.underwritingLimit),
        claimAdviseLimit: Number(typedValues.claimadviseLimit),
        substandardLimit:
          typedValues.treatyType?.toLowerCase() === 'proportional' &&
          typedValues.substandardLimit
            ? Number(typedValues.substandardLimit)
            : null,
        reinsurers: typedValues.reinsurersRepeater.map((r, idx) => ({
          isLeader: idx === 0,
          reinsurerID: r.reinsurer,
          reinsurerShare: Number(r.share)
        }))
      }
    };

    const result = await createTreaty(payload);
    const treatyId = result.data.reinsurance.createTreaty;
    onSuccess(`/reinsurance/treaties/${treatyId}`);
  };

  // Don't render the form until sections are ready
  if (sections.length === 0) {
    return null;
  }

  return (
    <DynamicFormWidget
      sections={sections}
      onChange={handleChange}
      onSubmit={onSubmit}
      submitButtonState={submitButtonState}
      onAutocompleteSearch={handleSearch}
    />
  );
};

export { NewTreatyDrawer };
